import { useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';

export interface CustomCursorProps {
  /** Base diameter (px) of the follower ring. */
  size?: number;
}

/**
 * A gold cursor-follower ring that trails the pointer and swells when hovering
 * any element marked `data-cursor` (CTAs, media, interactive tiles) — a subtle,
 * premium "smart" touch. Mount ONCE per page. Renders nothing on touch devices
 * or under reduced motion, leaving the native cursor untouched.
 */
export function CustomCursor({ size = 22 }: CustomCursorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const dot = ref.current;
      if (!dot || reduced) return;
      if (window.matchMedia('(hover: none)').matches) return;

      gsap.set(dot, { xPercent: -50, yPercent: -50, opacity: 0 });
      const xTo = gsap.quickTo(dot, 'x', { duration: DURATION.fast, ease: GSAP_EASE.out });
      const yTo = gsap.quickTo(dot, 'y', { duration: DURATION.fast, ease: GSAP_EASE.out });

      let shown = false;
      const onMove = (event: PointerEvent) => {
        xTo(event.clientX);
        yTo(event.clientY);
        if (!shown) {
          shown = true;
          gsap.to(dot, { opacity: 1, duration: DURATION.fast });
        }
      };
      const onOver = (event: PointerEvent) => {
        const target = (event.target as HTMLElement)?.closest?.('[data-cursor]');
        gsap.to(dot, { scale: target ? 2.6 : 1, duration: DURATION.fast, ease: GSAP_EASE.spring });
      };
      const onLeaveWindow = () => gsap.to(dot, { opacity: 0, duration: DURATION.fast });

      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerover', onOver, { passive: true });
      document.addEventListener('pointerleave', onLeaveWindow);
      return () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerover', onOver);
        document.removeEventListener('pointerleave', onLeaveWindow);
      };
    },
    { scope: ref, dependencies: [reduced] },
  );

  // Never intercept clicks; hidden until the pointer moves.
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-0 z-[70] hidden rounded-full border border-brand-gold/70 opacity-0 mix-blend-difference lg:block"
      style={{ width: size, height: size }}
    />
  );
}
